import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCart, useAuth } from '../context/AppContext';
import API from '../utils/api';
import toast from 'react-hot-toast';

function Field({
  label,
  type = 'text',
  required,
  placeholder,
  value,
  onChange,
  children,
}) {
  return (
    <div className="form-group">
      <label className="form-label">
        {label}
        {required && ' *'}
      </label>

      {children || (
        <input
          className="form-input"
          type={type}
          value={value}
          onChange={onChange}
          placeholder={placeholder}
          required={required}
        />
      )}
    </div>
  );
}

export default function Checkout() {
  const { cart, cartSubtotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [payMethod] = useState('razorpay');
  const [placing, setPlacing] = useState(false);

  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone ? String(user.phone).replace(/^91/, '') : '',
    email: user?.email || '',
    line1: '',
    line2: '',
    city: '',
    state: 'Tamil Nadu',
    pincode: '',
  });

  useEffect(() => {
    if (!user) {
      toast.error('Please login to continue checkout');
      navigate('/login');
    }
  }, [user, navigate]);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
  };

  const calculateShipping = (state) => {
    if (!state) return 100;
    return state.toLowerCase().trim() === 'tamil nadu' ? 60 : 100;
  };

  const shippingCharge = calculateShipping(form.state);
  const totalAmount = cartSubtotal + shippingCharge;

  const cancelOrder = async (orderId) => {
    try {
      await API.put(`/orders/${orderId}/cancel`);
    } catch (err) {
      console.log('Cancel order failed:', err.response?.data || err.message);
    }
  };

  const handlePlace = async () => {
    if (!user) {
      toast.error('Please login to place order');
      navigate('/login');
      return;
    }

    if (!form.name || !form.phone || !form.line1 || !form.city || !form.pincode) {
      return toast.error('Please fill all required fields');
    }

    if (form.phone.length !== 10) {
      return toast.error('Enter valid 10-digit mobile number');
    }

    if (form.pincode.length !== 6) {
      return toast.error('Enter valid 6-digit pincode');
    }

    if (cart.length === 0) {
      return toast.error('Your cart is empty');
    }

    if (!window.Razorpay) {
      return toast.error('Razorpay is not loaded. Please refresh and try again.');
    }

    setPlacing(true);

    let createdOrder = null;

    try {
      const orderPayload = {
        items: cart.map((i) => ({
          product: i._id,
          name: i.name,
          price: i.discountPrice || i.price,
          image: i.images?.[0]?.url || '',
          qty: i.qty,
          category: i.category,
        })),
        address: {
          fullName: form.name,
          phone: form.phone,
          line1: form.line1,
          line2: form.line2,
          city: form.city,
          state: form.state,
          pincode: form.pincode,
        },
        subtotal: cartSubtotal,
        shippingCharge,
        totalAmount,
        paymentMethod: payMethod,
      };

      const { data: order } = await API.post('/orders', orderPayload);
      createdOrder = order;

      const { data: rzpOrder } = await API.post('/payment/create-order', {
  amount: totalAmount * 100,
  customerName: form.name,
  customerPhone: form.phone,
  customerEmail: form.email || "",
  websiteOrderId: order.orderId || order._id,
});

const { data: keyData } = await API.get('/payment/key');

if (rzpOrder.demo) {
        toast.error('Razorpay keys are missing. Demo payment disabled.');
        await cancelOrder(order._id);
        return;
      }

      const options = {
        key: keyData.key,
        amount: rzpOrder.amount,
        currency: 'INR',
        name: 'Anvith Dazzle',
        description: 'Jewellery Purchase',
        order_id: rzpOrder.id,
        prefill: {
          name: form.name,
          contact: form.phone,
          email: form.email,
        },
        theme: { color: '#6B1B2A' },

        modal: {
          ondismiss: async () => {
            await cancelOrder(order._id);
            toast.error('Payment cancelled. Order not placed.');
            setPlacing(false);
          },
        },

        handler: async (response) => {
          try {
            const { data } = await API.post('/payment/verify', {
              ...response,
              orderId: order._id,
            });

            if (!data.success) {
              await cancelOrder(order._id);
              toast.error('Payment verification failed');
              setPlacing(false);
              return;
            }

            clearCart();

            navigate(`/order-success/${order._id}`, {
              state: {
                order: {
                  ...order,
                  paymentStatus: 'paid',
                  orderStatus: 'confirmed',
                  shippingCharge,
                  totalAmount,
                },
                paymentId: response.razorpay_payment_id,
              },
            });
          } catch (err) {
            await cancelOrder(order._id);
            toast.error(err.response?.data?.message || 'Payment verification failed');
            setPlacing(false);
          }
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', async (response) => {
        await cancelOrder(order._id);
        toast.error(response.error?.description || 'Payment failed');
        setPlacing(false);
      });

      razorpay.open();
    } catch (err) {
      if (createdOrder?._id) {
        await cancelOrder(createdOrder._id);
      }

      toast.error(err.response?.data?.message || 'Order failed. Please try again.');
      setPlacing(false);
    }
  };

  return (
    <div className="checkout-page">
      <div className="checkout-main">
        <h1 className="checkout-title">Checkout</h1>

        <div className="checkout-card">
          <h3 className="section-title">Contact Information</h3>

          <Field
            label="Full Name"
            value={form.name}
            onChange={(e) => set('name', e.target.value)}
            required
            placeholder="Your full name"
          />

          <div className="form-row">
            <Field
              label="Mobile Number"
              value={form.phone}
              onChange={(e) => set('phone', e.target.value.replace(/\D/g, '').slice(0, 10))}
              required
              placeholder="99999 99999"
            />

            <Field
              label="Email"
              type="email"
              value={form.email}
              onChange={(e) => set('email', e.target.value)}
              placeholder="email@example.com"
            />
          </div>

          <h3 className="section-title address-title">Delivery Address</h3>

          <Field
            label="Address Line 1"
            value={form.line1}
            onChange={(e) => set('line1', e.target.value)}
            required
            placeholder="House/Flat, Street"
          />

          <Field
            label="Address Line 2"
            value={form.line2}
            onChange={(e) => set('line2', e.target.value)}
            placeholder="Area, Landmark (optional)"
          />

          <div className="form-row">
            <Field
              label="City"
              value={form.city}
              onChange={(e) => set('city', e.target.value)}
              required
              placeholder="Chennai"
            />

            <Field
              label="Pincode"
              value={form.pincode}
              onChange={(e) => set('pincode', e.target.value.replace(/\D/g, '').slice(0, 6))}
              required
              placeholder="600001"
            />
          </div>

          <Field label="State">
            <select
              className="form-input"
              value={form.state}
              onChange={(e) => set('state', e.target.value)}
            >
              {[
                'Tamil Nadu',
                'Karnataka',
                'Maharashtra',
                'Delhi',
                'Kerala',
                'Andhra Pradesh',
                'Telangana',
                'Gujarat',
                'Rajasthan',
                'Other',
              ].map((s) => (
                <option key={s}>{s}</option>
              ))}
            </select>
          </Field>

          <h3 className="section-title payment-title">Payment Method</h3>

          <div className="payment-box">
            <label className="payment-option active">
              <input
                type="radio"
                name="pay"
                value="razorpay"
                checked
                readOnly
              />

              <div className="payment-icon">💳</div>

              <div className="payment-content">
                <div className="payment-name">Pay Online Securely</div>
                <div className="payment-desc">
                  UPI, Cards, Net Banking & Wallets via Razorpay
                </div>
              </div>

              <div className="payment-badge">Recommended</div>
            </label>
          </div>

          <button
            className="btn btn-primary btn-full btn-lg place-order-btn"
            onClick={handlePlace}
            disabled={placing}
          >
            {placing ? 'Processing...' : `Pay ₹${totalAmount.toLocaleString()} Securely`}
          </button>

          <p className="secure-note">🔒 100% Secure Payment | SSL Encrypted</p>
        </div>
      </div>

      <div className="order-summary">
        <h3 className="summary-title">Order Summary</h3>

        {cart.map((item) => (
          <div key={item._id} className="summary-item">
            <div className="summary-img">
              {item.images?.[0]?.url ? (
                <img src={item.images[0].url} alt="" />
              ) : (
                <div className="fallback-img">💍</div>
              )}
            </div>

            <div className="summary-info">
              <div className="summary-name">{item.name}</div>
              <div className="summary-qty">Qty: {item.qty}</div>
            </div>

            <div className="summary-price">
              ₹{((item.discountPrice || item.price) * item.qty).toLocaleString()}
            </div>
          </div>
        ))}

        <div className="price-box">
          <div className="price-row">
            <span>Subtotal</span>
            <span>₹{cartSubtotal.toLocaleString()}</span>
          </div>

          <div className="price-row">
            <span>Shipping</span>
            <span>₹{shippingCharge.toLocaleString()}</span>
          </div>

          <div className="total-row">
            <span>Total</span>
            <span>₹{totalAmount.toLocaleString()}</span>
          </div>
        </div>
      </div>

      <style>{`
        .checkout-page {
          max-width: 1100px;
          margin: 0 auto;
          padding: 48px 24px;
          display: grid;
          grid-template-columns: 1fr 380px;
          gap: 40px;
          align-items: start;
        }

        .checkout-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 38px;
          font-weight: 300;
          margin-bottom: 28px;
          color: var(--maroon);
        }

        .checkout-card {
          background: white;
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 34px;
          box-shadow: 0 14px 40px rgba(107, 27, 42, 0.07);
        }

        .section-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 24px;
          font-weight: 400;
          margin-bottom: 20px;
          color: var(--maroon);
        }

        .address-title,
        .payment-title {
          margin-top: 28px;
        }

        .form-row {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 16px;
        }

        .payment-box {
          margin-bottom: 24px;
        }

        .payment-option {
          position: relative;
          display: flex;
          align-items: center;
          gap: 16px;
          padding: 18px;
          border: 1.5px solid var(--maroon);
          border-radius: 14px;
          background: linear-gradient(135deg, #FFF8F8, #FFFFFF);
          cursor: pointer;
          box-shadow: 0 10px 28px rgba(107, 27, 42, 0.08);
        }

        .payment-option input {
          width: 18px;
          height: 18px;
          accent-color: #6B1B2A;
        }

        .payment-icon {
          width: 48px;
          height: 48px;
          border-radius: 50%;
          background: var(--maroon);
          color: white;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
          flex-shrink: 0;
        }

        .payment-content {
          flex: 1;
          text-align: left;
        }

        .payment-name {
          font-size: 15px;
          font-weight: 600;
          color: var(--text-dark);
          margin-bottom: 4px;
        }

        .payment-desc {
          font-size: 12px;
          color: var(--text-light);
          line-height: 1.4;
        }

        .payment-badge {
          font-size: 11px;
          padding: 5px 10px;
          border-radius: 30px;
          background: var(--maroon);
          color: white;
          white-space: nowrap;
        }

        .place-order-btn {
          margin-top: 4px;
        }

        .secure-note {
          text-align: center;
          font-size: 12px;
          color: var(--text-light);
          margin-top: 14px;
        }

        .order-summary {
          background: var(--cream);
          border: 1px solid var(--border);
          border-radius: 14px;
          padding: 24px;
          position: sticky;
          top: 80px;
          box-shadow: 0 14px 40px rgba(107, 27, 42, 0.06);
        }

        .summary-title {
          font-family: 'Cormorant Garamond', serif;
          font-size: 24px;
          font-weight: 400;
          margin-bottom: 16px;
          color: var(--maroon);
        }

        .summary-item {
          display: flex;
          gap: 12px;
          padding: 12px 0;
          border-bottom: 1px solid var(--border);
        }

        .summary-img {
          width: 56px;
          height: 56px;
          border-radius: 10px;
          overflow: hidden;
          background: white;
          flex-shrink: 0;
        }

        .summary-img img {
          width: 100%;
          height: 100%;
          object-fit: cover;
        }

        .fallback-img {
          width: 100%;
          height: 100%;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 22px;
        }

        .summary-info {
          flex: 1;
        }

        .summary-name {
          font-family: 'Cormorant Garamond', serif;
          font-size: 15px;
          color: var(--text-dark);
        }

        .summary-qty {
          font-size: 12px;
          color: var(--text-light);
          margin-top: 4px;
        }

        .summary-price {
          font-size: 14px;
          font-weight: 600;
          color: var(--maroon);
        }

        .price-box {
          margin-top: 16px;
        }

        .price-row {
          display: flex;
          justify-content: space-between;
          padding: 9px 0;
          font-size: 14px;
          border-bottom: 1px solid var(--border);
        }

        .total-row {
          display: flex;
          justify-content: space-between;
          padding: 15px 0 4px;
          font-size: 19px;
          font-weight: 600;
          color: var(--maroon);
        }

        @media(max-width: 768px) {
          .checkout-page {
            grid-template-columns: 1fr;
            padding: 32px 16px;
          }

          .checkout-card {
            padding: 24px;
          }

          .form-row {
            grid-template-columns: 1fr;
            gap: 0;
          }

          .order-summary {
            position: static;
          }

          .payment-option {
            align-items: flex-start;
            flex-wrap: wrap;
          }

          .payment-badge {
            margin-left: 34px;
          }
        }
      `}</style>
    </div>
  );
}